import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  Typography,
  Button,
  Input,
  Row,
  Col,
  Image,
  Tag,
  Empty,
  Spin,
  Select,
  Space,
  Flex,
  notification,
} from 'antd';
import { EraserIcon, MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { updateStylingWorkflowCreatePayload } from '../../../store/stylist/slice';
import { useSearchStylesInfiniteQuery } from '../../../store/stylesSearch/stylesSearchApi';
import { useFetchOnListEnd } from '@admin-panel/hooks';
import { updateAttributesOfCdnImage } from '@admin-panel/utils';
import type {
  RecommendedFacetDTOType,
  RecommendedProductDTOType,
} from '../../../store/stylesSearch/schemas';

const { Title, Text } = Typography;

interface ProductImageSelectorProps {
  imageUrl: string;
  isSelected: boolean;
  onClick: () => void;
  width?: number;
  height?: number;
}

const ProductImageSelector = ({
  imageUrl,
  isSelected,
  onClick,
  width = 160,
  height = 240,
}: ProductImageSelectorProps) => {
  return (
    <div
      style={{
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        transform: isSelected ? 'scale(0.95)' : 'scale(1)',
      }}
      onClick={onClick}
    >
      <Image
        width={width}
        height={height}
        src={updateAttributesOfCdnImage(imageUrl, {
          width: 720,
          quality: 100,
        })}
        style={{
          objectFit: 'cover',
          borderRadius: 8,
          border: isSelected ? '3px solid #1890ff' : '2px solid #f0f0f0',
          boxShadow: isSelected
            ? '0 4px 12px rgba(24, 144, 255, 0.3)'
            : '0 2px 8px rgba(0,0,0,0.1)',
        }}
        preview={false}
        fallback="https://placehold.co/160x240?text=No+Image"
      />

      {isSelected && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: '#1890ff',
            color: 'white',
            borderRadius: '50%',
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          ✓
        </div>
      )}
    </div>
  );
};

interface SelectedProductItem {
  base_product_id: string;
  lk_style_number?: string;
  base_product_type: string;
  source_image_url: string;
  base_product_handle: string; // Shopify handle for the product
  base_product_variant_id?: string;
  base_product_color: string;
  base_product_brand_name?: string;
  base_product_name?: string;
  image_generation_model?: string;
}

const readArrayFirst = (value?: Array<string | number | boolean | null>): string => {
  const first = value?.[0];
  if (typeof first === 'string') {
    return first;
  }
  if (typeof first === 'number') {
    return first.toString();
  }
  return '';
};

const getPrimaryVariant = (product: RecommendedProductDTOType) => product.variants?.[0];

const getVariantSelectionKey = (product: RecommendedProductDTOType) => {
  const variant = getPrimaryVariant(product);
  return (
    readArrayFirst(variant?.variant_id) ||
    readArrayFirst(variant?.sku_code) ||
    readArrayFirst(variant?.size) ||
    product.product_id
  );
};

const getStyleSearchImages = (product: RecommendedProductDTOType): Array<string> => {
  const variantImages = product.variants.flatMap(variant => variant.images ?? []);
  return Array.from(
    new Set([...(product.images ?? []), ...variantImages, product.default_image]),
  ).filter(Boolean);
};

const ProductSelection = () => {
  const dispatch = useAppDispatch();
  const workflowPayload = useAppSelector(state => state.stylist.stylingWorkflowCreatePayload);

  const [searchText, setSearchText] = useState('');
  const [activeSearchText, setActiveSearchText] = useState('');
  const [selectedFacets, setSelectedFacets] = useState<Record<string, Array<string>>>({});
  const searchPayload = useMemo(() => {
    const selectedFacetsPayload =
      Object.keys(selectedFacets).length > 0 ? selectedFacets : undefined;

    return {
      searchType: 'FREETEXT' as const,
      text: activeSearchText.trim() || '',
      selectedFacets: selectedFacetsPayload,
    };
  }, [activeSearchText, selectedFacets]);

  const {
    data: searchData,
    isLoading: productsLoading,
    isError: isProductsError,
    error: productsError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch: refetchProducts,
  } = useSearchStylesInfiniteQuery(searchPayload);

  const products = useMemo(
    () =>
      (searchData?.pages?.flatMap(page => page.products || []).filter(Boolean) ||
        []) as Array<RecommendedProductDTOType>,
    [searchData],
  );

  const facets = useMemo(() => searchData?.pages?.[0]?.facets ?? [], [searchData]);

  const { lastItemRef } = useFetchOnListEnd(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  const items = workflowPayload?.items || [];

  const handleSearch = () => {
    setActiveSearchText(searchText);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setActiveSearchText('');
    setSelectedFacets({});
  };

  const handleDeleteProduct = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    dispatch(updateStylingWorkflowCreatePayload({ items: newItems }));
  };

  const handleProductImageSelect = (product: RecommendedProductDTOType, imageUrl: string) => {
    const variantSelectionKey = getVariantSelectionKey(product);
    const isCurrentlySelected = isProductSelected(product, imageUrl);

    if (isCurrentlySelected) {
      // Remove if already selected
      const newItems = items.filter(
        item =>
          !(
            item.base_product_id === product.product_id &&
            (item.base_product_variant_id ?? '') === variantSelectionKey &&
            item.source_image_url === imageUrl
          ),
      );

      // If no items left, just update with empty items array
      if (newItems.length === 0) {
        dispatch(updateStylingWorkflowCreatePayload({ items: newItems }));
      } else {
        dispatch(updateStylingWorkflowCreatePayload({ items: newItems }));
      }
    } else {
      // Remove any existing selection for this product+variant combination
      const filteredItems = items.filter(
        item =>
          !(
            item.base_product_id === product.product_id &&
            (item.base_product_variant_id ?? '') === variantSelectionKey
          ),
      );

      const newItem: SelectedProductItem = {
        base_product_id: product.product_id,
        lk_style_number: product.lk_style_number || undefined,
        base_product_type: product.product_type || 'unknown',
        source_image_url: imageUrl,
        base_product_handle: product.handle,
        base_product_variant_id: variantSelectionKey || undefined,
        base_product_color: product.color || 'unknown',
        base_product_brand_name: product.brand || '',
        base_product_name: product.title || '',
        image_generation_model: workflowPayload?.image_generation_model,
      };

      const newItems = [...filteredItems, newItem];

      // Just update the items array - no need for workflow-level base product fields
      dispatch(updateStylingWorkflowCreatePayload({ items: newItems }));
    }
  };

  const isProductSelected = (product: RecommendedProductDTOType, imageUrl: string) => {
    const variantSelectionKey = getVariantSelectionKey(product);
    return items.some(
      item =>
        item.base_product_id === product.product_id &&
        (item.base_product_variant_id ?? '') === variantSelectionKey &&
        item.source_image_url === imageUrl,
    );
  };

  const hasActiveFilters = activeSearchText.trim() || Object.keys(selectedFacets).length > 0;

  // Show toast notification for errors instead of breaking the component
  useEffect(() => {
    if (isProductsError && productsError) {
      notification.error({
        message: 'Error loading products',
        description: 'Failed to load products. Please try again or clear filters.',
        duration: 4,
        placement: 'topRight',
        btn: (
          <Button
            size="small"
            type="primary"
            onClick={() => {
              refetchProducts();
              notification.destroy();
            }}
          >
            Retry
          </Button>
        ),
      });
    }
  }, [isProductsError, productsError, refetchProducts]);

  return (
    <Card style={{ margin: '16px 0' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Product Selection</Title>
          <Text type="secondary">
            Search and select product images for styling workflow. Each selected image will be
            processed individually.
          </Text>
        </div>

        <div
          style={{
            padding: 16,
            backgroundColor: '#fafafa',
            borderRadius: 8,
          }}
        >
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search products..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={handleKeyPress}
                prefix={<MagnifyingGlassIcon />}
                allowClear
              />
            </Col>
            {facets.map((facet: RecommendedFacetDTOType) => (
              <Col xs={24} sm={12} md={8} key={facet.field_name}>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder={`Select ${facet.field_name}`}
                  style={{ width: '100%' }}
                  value={selectedFacets[facet.field_name] || []}
                  options={(facet.counts ?? []).map((v: { value: string; count: number }) => ({
                    label: (
                      <Flex justify="space-between">
                        <span>{v.value}</span>
                        <span>({v.count || 0})</span>
                      </Flex>
                    ),
                    value: v.value,
                  }))}
                  onChange={values =>
                    setSelectedFacets(prev => ({
                      ...prev,
                      [facet.field_name]: values,
                    }))
                  }
                  maxTagCount="responsive"
                />
              </Col>
            ))}

            <Col xs={24} sm={24} md={10}>
              <Space size="small">
                <Button type="primary" onClick={handleSearch} icon={<MagnifyingGlassIcon />}>
                  Search
                </Button>
                {hasActiveFilters && (
                  <Button onClick={handleClearFilters} icon={<EraserIcon />}>
                    Clear
                  </Button>
                )}
              </Space>
            </Col>
          </Row>

          {hasActiveFilters && (
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid #e8e8e8',
              }}
            >
              <Space size="small" wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Filters:
                </Text>
                {activeSearchText.trim() && (
                  <Tag
                    closable
                    onClose={() => {
                      setSearchText('');
                      setActiveSearchText('');
                    }}
                  >
                    "{activeSearchText}"
                  </Tag>
                )}

                {Object.entries(selectedFacets).map(([facetKey, values]) =>
                  values.map(val => (
                    <Tag
                      key={`${facetKey}-${val}`}
                      closable
                      onClose={() => {
                        setSelectedFacets(prev => {
                          const updated = { ...prev };
                          updated[facetKey] = updated[facetKey].filter(v => v !== val);
                          if (updated[facetKey].length === 0) {
                            delete updated[facetKey];
                          }
                          return updated;
                        });
                      }}
                    >
                      {facets
                        .find(f => f.field_name === facetKey)
                        ?.counts?.find(v => v.value === val)?.value || val}
                    </Tag>
                  )),
                )}
              </Space>
            </div>
          )}
        </div>

        <div>
          {productsLoading && products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
              <div style={{ marginTop: 12 }}>
                <Text type="secondary">Loading products...</Text>
              </div>
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Empty
                description={
                  activeSearchText || Object.keys(selectedFacets).length > 0
                    ? 'No products found with current filters'
                    : 'Start searching to find products'
                }
              />
              {hasActiveFilters && (
                <Button type="link" onClick={handleClearFilters} style={{ marginTop: 8 }}>
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 24,
                maxHeight: '600px',
                overflowY: 'auto',
                paddingRight: '8px',
                marginRight: '-8px',
                scrollbarWidth: 'thin',
                scrollbarColor: '#d4d4d4 transparent',
              }}
            >
              {products.map((product, index) => {
                const availableImages = getStyleSearchImages(product);
                const variant = getPrimaryVariant(product);

                return (
                  <div
                    key={`${product.product_id}-${product.color || 'default'}`}
                    ref={index === products.length - 1 ? lastItemRef : undefined}
                    style={{
                      padding: '16px 0',
                      borderBottom: index < products.length - 1 ? '1px solid #f0f0f0' : 'none',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 16,
                      }}
                    >
                      <Tag>{product.product_type}</Tag>
                      <Text style={{ fontSize: 16 }}>{product.brand} |</Text>
                      <Text strong style={{ fontSize: 16 }}>
                        {product.title}
                      </Text>

                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {product.lk_style_number || ''}
                      </Text>
                      {variant ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {readArrayFirst(variant.sku_code)}
                        </Text>
                      ) : null}
                    </div>

                    <Row gutter={[12, 12]}>
                      {availableImages.map((image, imgIndex) => {
                        const isSelected = isProductSelected(product, image);
                        return (
                          <Col key={`${image}-${imgIndex}`}>
                            <ProductImageSelector
                              imageUrl={image}
                              isSelected={isSelected}
                              onClick={() => handleProductImageSelect(product, image)}
                            />
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                );
              })}
            </div>
          )}

          {isFetchingNextPage && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Loading more products...</Text>
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div
            style={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'white',
              borderTop: '2px solid #f0f0f0',
              padding: '16px 0',
              marginTop: 32,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                Selected Images ({items.length})
              </Title>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                overflowX: 'auto',
                paddingBottom: 8,
                maxHeight: 140, // Increased for bigger images
              }}
            >
              {items.map((item, index) => (
                <div
                  key={`selected-${index}`}
                  style={{
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  <Image
                    width={80}
                    height={120}
                    src={updateAttributesOfCdnImage(item.source_image_url, {
                      width: 240, // 3x for high quality
                      quality: 100,
                    })}
                    style={{
                      objectFit: 'cover',
                      borderRadius: 6,
                      border: '2px solid #1890ff',
                    }}
                    preview={false}
                  />
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<XIcon />}
                    style={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: '#ff4d4f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      minWidth: 20,
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                    onClick={() => handleDeleteProduct(index)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default ProductSelection;
